import React, { useState, useEffect } from "react";
import { Card, Tab, Tabs, Table, Alert, Badge, Spinner, Row, Col, Form, Button, ButtonGroup } from "react-bootstrap";

import {
  getTodayStatsByGrade,
  getOverallStatsByGrade,
  getSchoolStatsByGrade,
  getNotStartedByGrade,
  getAssignedScreeners,
} from "../services/APIClient";

// Import xlsx for Excel export
import * as XLSX from 'xlsx';

function GradeReports() {
  const [activeTab, setActiveTab] = useState("todayByGrade");
  
  // Filter state
  const [filters, setFilters] = useState({
    classDateFrom: '2024-08-15',
    assessmentDateFrom: '2025-04-01',
    purchasedOnly: true,
    screeningStatus: ''
  });
  
  // State for the grade-related data
  const [todayByGradeData, setTodayByGradeData] = useState([]);
  const [overallByGradeData, setOverallByGradeData] = useState([]);
  const [overallBySchoolByGradeData, setOverallBySchoolByGradeData] = useState([]);
  const [notStartedByGradeData, setNotStartedByGradeData] = useState([]);
  const [assignedScreenersData, setAssignedScreenersData] = useState([]);
  
  // Loading and error states
  const [loadingStates, setLoadingStates] = useState({
    today: false,
    overall: false,
    schools: false,
    notStarted: false,
    screeners: false
  });
  const [errorMessages, setErrorMessages] = useState({});

  // Helper function to update loading state
  const setLoading = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  // Helper function to set error messages
  const setError = (key, message) => {
    setErrorMessages(prev => ({ ...prev, [key]: message }));
  };

  // Helper function to clear error messages
  const clearError = (key) => {
    setErrorMessages(prev => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  };

  // Helper function to update filters
  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fetchTodayByGrade = async (customFilters = null) => {
    try {
      setLoading('today', true);
      clearError('today');
      const filterOptions = customFilters || filters;
      const response = await getTodayStatsByGrade(filterOptions);
      setTodayByGradeData(response);
      console.log("Today by grade data loaded:", response);
    } catch (error) {
      console.error("Failed to fetch today by grade:", error);
      setError('today', 'Failed to load today\'s data. Please try again.');
    } finally {
      setLoading('today', false);
    }
  };

  const fetchOverallByGrade = async (customFilters = null) => {
    try {
      setLoading('overall', true);
      clearError('overall');
      const filterOptions = customFilters || filters;
      const response = await getOverallStatsByGrade(filterOptions);
      setOverallByGradeData(response);
      console.log("Overall by grade data loaded:", response);
    } catch (error) {
      console.error("Failed to fetch overall by grade:", error);
      setError('overall', 'Failed to load overall data. Please try again.');
    } finally {
      setLoading('overall', false);
    }
  };

  const fetchOverallBySchoolByGrade = async (customFilters = null) => {
    try {
      setLoading('schools', true);
      clearError('schools');
      const filterOptions = customFilters || filters;
      const response = await getSchoolStatsByGrade(filterOptions);
      setOverallBySchoolByGradeData(response);
      console.log("School by grade data loaded:", response);
    } catch (error) {
      console.error("Failed to fetch overall by school by grade:", error);
      setError('schools', 'Failed to load school data. Please try again.');
    } finally {
      setLoading('schools', false);
    }
  };

  const fetchNotStartedByGrade = async (customFilters = null) => {
    try {
      setLoading('notStarted', true);
      clearError('notStarted');
      const filterOptions = customFilters || filters;
      const response = await getNotStartedByGrade(filterOptions);
      setNotStartedByGradeData(response);
      console.log("Not started by grade data loaded:", response);
    } catch (error) {
      console.error("Failed to fetch not started by grade:", error);
      setError('notStarted', 'Failed to load not started data. Please try again.');
    } finally {
      setLoading('notStarted', false);
    }
  };

  const fetchAssignedScreeners = async (customFilters = null) => {
    try {
      setLoading('screeners', true);
      clearError('screeners');
      const filterOptions = customFilters || filters;
      const response = await getAssignedScreeners(filterOptions);
      setAssignedScreenersData(response);
      console.log("Assigned screeners data loaded:", response);
    } catch (error) {
      console.error("Failed to fetch assigned screeners:", error);
      setError('screeners', 'Failed to load screener assignment data. Please try again.');
    } finally {
      setLoading('screeners', false);
    }
  };

  // Auto-fetch data when component mounts
  useEffect(() => {
    fetchTodayByGrade();
    fetchOverallByGrade();
  }, []);

  // Apply filters based on current active tab
  const handleApplyFilters = () => {
    if (activeTab === 'todayByGrade') {
      fetchTodayByGrade();
    } else if (activeTab === 'overallByGrade') {
      fetchOverallByGrade();
    } else if (activeTab === 'overallBySchoolByGrade') {
      fetchOverallBySchoolByGrade();
    } else if (activeTab === 'notStarted') {
      fetchNotStartedByGrade();
    } else if (activeTab === 'screenersAssigned') {
      fetchAssignedScreeners();
    }
  };

  // Export functions using xlsx library
  const handleExport = (reportType, data) => {
    let exportData = [];
    let filename = '';
    
    switch (reportType) {
      case 'today':
        exportData = todayByGradeData;
        filename = 'today-stats-by-grade';
        break;
      case 'overall':
        exportData = overallByGradeData;
        filename = 'overall-stats-by-grade';
        break;
      case 'schools':
        exportData = overallBySchoolByGradeData;
        filename = 'school-stats-by-grade';
        break;
      case 'not-started':
        exportData = notStartedByGradeData;
        filename = 'not-started-schools';
        break;
      case 'screeners':
        exportData = assignedScreenersData;
        filename = 'assigned-screeners';
        break;
      default:
        console.error('Unknown export type:', reportType);
        return;
    }

    if (!exportData || exportData.length === 0) {
      alert('No data available to export');
      return;
    }

    try {
      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Add metadata sheet
      const metadata = [{
        report_type: reportType,
        generated_at: new Date().toISOString(),
        class_date_from: filters.classDateFrom,
        assessment_date_from: filters.assessmentDateFrom,
        purchased_only: filters.purchasedOnly,
        screening_status: filters.screeningStatus || 'All',
        total_records: exportData.length
      }];
      
      const metadataSheet = XLSX.utils.json_to_sheet(metadata);
      XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Report Info');
      
      // Add data sheet
      const dataSheet = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(workbook, dataSheet, 'Data');
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const fullFilename = `${filename}-${timestamp}.xlsx`;
      
      // Download file
      XLSX.writeFile(workbook, fullFilename);
      
      console.log(`Exported ${exportData.length} records to ${fullFilename}`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Helper function to calculate summary statistics from table data
  const calculateSummaryStats = (data) => {
    // Handle case where data might be an object or not an array
    if (!data) return null;
    
    // If data is not an array, return null (no summary stats)
    if (!Array.isArray(data)) {
      console.log("Data is not an array, skipping summary stats:", data);
      return null;
    }
    
    if (data.length === 0) return null;
    
    const totalGrade1 = data.reduce((sum, school) => sum + (school.grade_level1_students || 0), 0);
    const totalGrade2 = data.reduce((sum, school) => sum + (school.grade_level2_students || 0), 0);
    const totalSubmissions = data.reduce((sum, school) => sum + (school.students_with_submissions_today || school.students_with_submissions || school.total_students_with_submissions || 0), 0);
    const totalAssessments = data.reduce((sum, school) => sum + (school.assessments_assigned_today || school.total_assessments_assigned || school.total_screeners_assigned || 0), 0);
    
    return {
      grade_level1_students: totalGrade1,
      grade_level2_students: totalGrade2,
      students_with_submissions: totalSubmissions,
      total_assessments: totalAssessments
    };
  };

  // Helper function to render summary stats from calculated totals
  const renderSummaryStats = (data, type) => {
    const stats = calculateSummaryStats(data);
    if (!stats) return null;
    
    return (
      <Row className="mb-3">
        <Col md={3}>
          <Card className="text-center border-primary">
            <Card.Body>
              <h4 className="text-primary">{stats.grade_level1_students}</h4>
              <small className="text-muted">Total Grade 1 Students</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-info">
            <Card.Body>
              <h4 className="text-info">{stats.grade_level2_students}</h4>
              <small className="text-muted">Total Grade 2 Students</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-success">
            <Card.Body>
              <h4 className="text-success">{stats.students_with_submissions}</h4>
              <small className="text-muted">Students with Submissions</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-warning">
            <Card.Body>
              <h4 className="text-warning">{stats.total_assessments}</h4>
              <small className="text-muted">
                {type === 'today' ? 'Assessments Today' : 
                 type === 'screeners' ? 'Screeners Assigned' : 'Total Assessments'}
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  // Enhanced helper function to render school table with more columns
  const renderSchoolTable = (data, reportType = 'basic') => {
    // Handle case where data might not be an array
    if (!data) return <p className="text-muted">No data available</p>;
    
    // If data is not an array, try to extract array from object structure
    let tableData = data;
    if (!Array.isArray(data)) {
      // Check if it's an object with an array property
      if (data.detailed_data && Array.isArray(data.detailed_data.school_breakdown)) {
        tableData = data.detailed_data.school_breakdown;
      } else if (data.schools && Array.isArray(data.schools)) {
        tableData = data.schools;
      } else {
        console.log("Data is not in expected format:", data);
        return <p className="text-muted">Data format not recognized. Expected an array of schools.</p>;
      }
    }
    
    if (tableData.length === 0) return <p className="text-muted">No schools found matching the current filters</p>;

    const getTableHeaders = () => {
      const baseHeaders = [
        "School Name",
        "Roll Number", 
        "Area",
        "Grade 1",
        "Grade 2",
        "Screening Status"
      ];
      
      switch (reportType) {
        case 'today':
          return [...baseHeaders, "Today's Assessments", "Started Today", "Completed Today", "Contact"];
        case 'overall':
          return [...baseHeaders, "Total Assigned", "Completed", "Completion %", "Screeners Assigned", "Contact"];
        case 'schools':
          return [...baseHeaders, "Total Assessments", "Submissions", "Individual Screeners", "Contact"];
        case 'notStarted':
          return [...baseHeaders, "Reason Not Started", "Contact"];
        case 'screeners':
          return [...baseHeaders, "Baseline", "Midpoint", "End of Year", "Total Assigned", "Contact"];
        default:
          return [...baseHeaders, "Submissions", "Contact"];
      }
    };

    const renderTableCells = (school, reportType) => {
      const baseCells = [
        <td key="name">{school.school_name}</td>,
        <td key="roll">{school.school_rollnumber}</td>,
        <td key="area">{school.school_area}</td>,
        <td key="grade1"><Badge bg="primary">{school.grade_level1_students || 0}</Badge></td>,
        <td key="grade2"><Badge bg="info">{school.grade_level2_students || 0}</Badge></td>,
        <td key="status">
          <Badge bg={school.screening_status === 'STARTED' ? 'success' : 'warning'}>
            {school.screening_status || 'Unknown'}
          </Badge>
        </td>
      ];

      const contactCell = (
        <td key="contact">
          <small>
            <strong>{school.teacher_name || 'No teacher assigned'}</strong><br/>
            {school.teacher_email && <>{school.teacher_email}<br/></>}
            {school.teacher_mobile && <>{school.teacher_mobile}</>}
          </small>
        </td>
      );

      switch (reportType) {
        case 'today':
          return [...baseCells,
            <td key="assigned"><Badge bg="secondary">{school.assessments_assigned_today || 0}</Badge></td>,
            <td key="started"><Badge bg="info">{school.assessments_started_today || 0}</Badge></td>,
            <td key="completed"><Badge bg="success">{school.assessments_completed_today || 0}</Badge></td>,
            contactCell
          ];
        case 'overall':
          return [...baseCells,
            <td key="assigned"><Badge bg="secondary">{school.total_assessments_assigned || 0}</Badge></td>,
            <td key="completed"><Badge bg="success">{school.total_assessments_completed || 0}</Badge></td>,
            <td key="completion">{school.completion_rate_percentage || 0}%</td>,
            <td key="screeners"><Badge bg="primary">{school.screeners_assigned || 0}</Badge></td>,
            contactCell
          ];
        case 'schools':
          return [...baseCells,
            <td key="assessments"><Badge bg="secondary">{school.total_assessments_since_filter_date || 0}</Badge></td>,
            <td key="submissions"><Badge bg="success">{school.total_students_with_submissions || 0}</Badge></td>,
            <td key="screeners">
              <small>
                B: {(school.junior_baseline_screener || 0) + (school.senior_baseline_screener || 0)}<br/>
                M: {(school.junior_midpoint_screener || 0) + (school.senior_midpoint_screener || 0)}<br/>
                E: {(school.junior_end_of_year_screener || 0) + (school.senior_end_of_year_screener || 0)}
              </small>
            </td>,
            contactCell
          ];
        case 'notStarted':
          return [...baseCells,
            <td key="reason">
              <small className="text-muted">
                {school.screeners_assigned > 0 ? 'Screeners assigned but not started' : 'No screeners assigned'}
              </small>
            </td>,
            contactCell
          ];
        case 'screeners':
          return [...baseCells,
            <td key="baseline">
              <small>
                Jr: {school.junior_baseline_assigned || 0}<br/>
                Sr: {school.senior_baseline_assigned || 0}
              </small>
            </td>,
            <td key="midpoint">
              <small>
                Jr: {school.junior_midpoint_assigned || 0}<br/>
                Sr: {school.senior_midpoint_assigned || 0}
              </small>
            </td>,
            <td key="endyear">
              <small>
                Jr: {school.junior_end_of_year_assigned || 0}<br/>
                Sr: {school.senior_end_of_year_assigned || 0}
              </small>
            </td>,
            <td key="total"><Badge bg="primary">{school.total_screeners_assigned || 0}</Badge></td>,
            contactCell
          ];
        default:
          return [...baseCells,
            <td key="submissions"><Badge bg="secondary">{school.total_students_with_submissions || school.total_screeners_assigned || 0}</Badge></td>,
            contactCell
          ];
      }
    };

    return (
      <Table striped bordered hover responsive size="sm">
        <thead className="table-dark">
          <tr>
            {getTableHeaders().map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.map((school, index) => (
            <tr key={school.schoolid || school.school_id || index}>
              {renderTableCells(school, reportType)}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  // Enhanced filters panel with better descriptions
  const renderFiltersPanel = () => {
    return (
      <Card className="mb-3">
        <Card.Header>
          <h6 className="mb-0">Data Filters & Controls</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Class Activity Since Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.classDateFrom}
                  onChange={(e) => updateFilter('classDateFrom', e.target.value)}
                />
                <Form.Text className="text-muted">
                  Only include classes that were updated/created after this date
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Assessment Activity Since Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.assessmentDateFrom}
                  onChange={(e) => updateFilter('assessmentDateFrom', e.target.value)}
                />
                <Form.Text className="text-muted">
                  Only include assessments created after this date
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>School Type Filter</Form.Label>
                <Form.Select
                  value={filters.purchasedOnly}
                  onChange={(e) => updateFilter('purchasedOnly', e.target.value === 'true')}
                >
                  <option value="true">Purchased Schools Only</option>
                  <option value="false">All Schools (Including Trial)</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Filter by school subscription status
                </Form.Text>
              </Form.Group>
            </Col>
            {activeTab === 'overallBySchoolByGrade' && (
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Screening Progress Filter</Form.Label>
                  <Form.Select
                    value={filters.screeningStatus}
                    onChange={(e) => updateFilter('screeningStatus', e.target.value)}
                  >
                    <option value="">All Schools</option>
                    <option value="STARTED">Schools That Started Screening</option>
                    <option value="NOT_STARTED">Schools That Haven't Started</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Filter by screening participation
                  </Form.Text>
                </Form.Group>
              </Col>
            )}
            <Col md={2}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <div className="d-grid">
                  <Button variant="primary" onClick={handleApplyFilters}>
                    Refresh Data with Filters
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Apply current filter settings
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="grade-reports">
      {renderFiltersPanel()}
      
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="todayByGrade" title="Today's Activity by School">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5>Today's Screening Activity by School</h5>
                <small className="text-muted">
                  Shows each school's assessment activity for today only, broken down by grade level
                </small>
              </div>
              <ButtonGroup>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={fetchTodayByGrade}
                  disabled={loadingStates.today}
                >
                  {loadingStates.today ? <Spinner size="sm" /> : 'Refresh'}
                </Button>
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={() => handleExport('today')}
                  disabled={!todayByGradeData || todayByGradeData.length === 0}
                >
                  Export Excel
                </Button>
              </ButtonGroup>
            </Card.Header>
            <Card.Body>
              {errorMessages.today && (
                <Alert variant="danger">{errorMessages.today}</Alert>
              )}
              
              {loadingStates.today ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading today's school activity data...</p>
                </div>
              ) : (
                <>
                  {renderSummaryStats(todayByGradeData, 'today')}
                  {todayByGradeData.length > 0 ? (
                    <div>
                      <h6 className="mb-3">School-by-School Breakdown ({todayByGradeData.length} schools)</h6>
                      {renderSchoolTable(todayByGradeData, 'today')}
                    </div>
                  ) : (
                    <Alert variant="info">
                      No school activity found for today with the current filter settings.
                    </Alert>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="overallByGrade" title="Overall Activity by School">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5>Overall Screening Activity by School</h5>
                <small className="text-muted">
                  Shows each school's total assessment activity since the filter dates, with completion rates
                </small>
              </div>
              <ButtonGroup>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={fetchOverallByGrade}
                  disabled={loadingStates.overall}
                >
                  {loadingStates.overall ? <Spinner size="sm" /> : 'Refresh'}
                </Button>
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={() => handleExport('overall')}
                  disabled={!overallByGradeData || overallByGradeData.length === 0}
                >
                  Export Excel
                </Button>
              </ButtonGroup>
            </Card.Header>
            <Card.Body>
              {errorMessages.overall && (
                <Alert variant="danger">{errorMessages.overall}</Alert>
              )}
              
              {loadingStates.overall ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading overall school activity data...</p>
                </div>
              ) : (
                <>
                  {renderSummaryStats(overallByGradeData, 'overall')}
                  {overallByGradeData.length > 0 ? (
                    <div>
                      <h6 className="mb-3">School-by-School Breakdown ({overallByGradeData.length} schools)</h6>
                      {renderSchoolTable(overallByGradeData, 'overall')}
                    </div>
                  ) : (
                    <Alert variant="info">
                      No school activity found with the current filter settings.
                    </Alert>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="overallBySchoolByGrade" title="Detailed School Analysis">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5>Detailed School Analysis with Individual Screeners</h5>
                <small className="text-muted">
                  Shows detailed breakdown of each school's screening progress with individual screener types
                </small>
              </div>
              <ButtonGroup>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={fetchOverallBySchoolByGrade}
                  disabled={loadingStates.schools}
                >
                  {loadingStates.schools ? <Spinner size="sm" /> : 'Refresh'}
                </Button>
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={() => handleExport('schools')}
                  disabled={!overallBySchoolByGradeData || overallBySchoolByGradeData.length === 0}
                >
                  Export Excel
                </Button>
              </ButtonGroup>
            </Card.Header>
            <Card.Body>
              {errorMessages.schools && (
                <Alert variant="danger">{errorMessages.schools}</Alert>
              )}
              
              {loadingStates.schools ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading detailed school analysis...</p>
                </div>
              ) : (
                <>
                  {overallBySchoolByGradeData.length > 0 && (
                    <div className="mb-3">
                      <Badge bg="info" className="me-2">
                        Total Schools: {overallBySchoolByGradeData.length}
                      </Badge>
                      <Badge bg="success" className="me-2">
                        Schools Started: {overallBySchoolByGradeData.filter(s => s.screening_status === 'STARTED').length}
                      </Badge>
                      <Badge bg="warning" className="me-2">
                        Schools Not Started: {overallBySchoolByGradeData.filter(s => s.screening_status === 'NOT_STARTED').length}
                      </Badge>
                      <small className="text-muted">
                        B=Baseline, M=Midpoint, E=End of Year screeners
                      </small>
                    </div>
                  )}
                  {overallBySchoolByGradeData.length > 0 ? (
                    renderSchoolTable(overallBySchoolByGradeData, 'schools')
                  ) : (
                    <Alert variant="info">
                      No schools found with the current filter settings.
                    </Alert>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="notStarted" title="Schools Needing Follow-up">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5>Schools That Haven't Started Screening</h5>
                <small className="text-muted">Schools that need follow-up to begin their screening process</small>
              </div>
              <ButtonGroup>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={fetchNotStartedByGrade}
                  disabled={loadingStates.notStarted}
                >
                  {loadingStates.notStarted ? <Spinner size="sm" /> : 'Refresh'}
                </Button>
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={() => handleExport('not-started')}
                  disabled={!notStartedByGradeData || notStartedByGradeData.length === 0}
                >
                  Export Excel
                </Button>
              </ButtonGroup>
            </Card.Header>
            <Card.Body>
              {errorMessages.notStarted && (
                <Alert variant="danger">{errorMessages.notStarted}</Alert>
              )}
              
              {loadingStates.notStarted ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading schools needing follow-up...</p>
                </div>
              ) : (
                <>
                  {notStartedByGradeData.length > 0 && (
                    <Alert variant="warning" className="mb-3">
                      <strong>{notStartedByGradeData.length}</strong> schools haven't started screening yet. 
                      Use the contact information below for follow-up calls or emails.
                    </Alert>
                  )}
                  {notStartedByGradeData.length > 0 ? (
                    renderSchoolTable(notStartedByGradeData, 'notStarted')
                  ) : (
                    <Alert variant="success">
                      Excellent! All schools with the current filter settings have started their screening process.
                    </Alert>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="screenersAssigned" title="School Intentions (Screeners Assigned)">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5>School Intentions - Screener Assignments</h5>
                <small className="text-muted">Shows which schools have assigned screener assessments (indicates their intention to participate)</small>
              </div>
              <ButtonGroup>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={fetchAssignedScreeners}
                  disabled={loadingStates.screeners}
                >
                  {loadingStates.screeners ? <Spinner size="sm" /> : 'Refresh'}
                </Button>
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={() => handleExport('screeners')}
                  disabled={!assignedScreenersData || assignedScreenersData.length === 0}
                >
                  Export Excel
                </Button>
              </ButtonGroup>
            </Card.Header>
            <Card.Body>
              {errorMessages.screeners && (
                <Alert variant="danger">{errorMessages.screeners}</Alert>
              )}
              
              {loadingStates.screeners ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading screener assignment data...</p>
                </div>
              ) : (
                <>
                  {assignedScreenersData.length > 0 && (
                    <Alert variant="info" className="mb-3">
                      <strong>{assignedScreenersData.length}</strong> schools have assigned screener assessments, 
                      indicating their intention to participate in the screening program.
                      <br/>
                      <small>Jr = Junior/Grade 1, Sr = Senior/Grade 2</small>
                    </Alert>
                  )}
                  {renderSummaryStats(assignedScreenersData, 'screeners')}
                  {assignedScreenersData.length > 0 ? (
                    <div>
                      <h6 className="mb-3">Screener Assignments by School ({assignedScreenersData.length} schools)</h6>
                      {renderSchoolTable(assignedScreenersData, 'screeners')}
                    </div>
                  ) : (
                    <Alert variant="warning">
                      No schools have assigned screeners with the current filter settings. 
                      This may indicate low participation or restrictive filters.
                    </Alert>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
}

export default GradeReports;